import React, { useState } from 'react';
import { Plus, Trash2, Eye, EyeOff, AlertCircle, CheckCircle2, XCircle, Loader2, BookOpen, FileText, Info } from 'lucide-react';
import type { WizardState, WizardRegulatoryVendor, WizardCodeSetVendorId, WizardSelectedBook } from '../types';
import type { StepError } from '../validateStep';
import { api } from '../../../../apiClient';

interface Props {
  state: WizardState;
  onChange: (patch: Partial<WizardState>) => void;
  errors: StepError[];
}

// UI-only state, NOT persisted in the wizard draft. Re-derived per session.
type ValidationStatus = 'idle' | 'testing' | 'valid' | 'invalid';
interface VendorUiState {
  validation: ValidationStatus;
  validationMessage?: string;
  catalogLoading: boolean;
  catalogError?: string;
  catalog?: WizardSelectedBook[];
}
const EMPTY_UI: VendorUiState = { validation: 'idle', catalogLoading: false };

const VENDOR_OPTIONS: { id: WizardCodeSetVendorId; label: string; description: string }[] = [
  { id: 'icc', label: 'ICC Digital Codes', description: 'International Code Council — IFC, IBC, IRC, and related codes.' },
  { id: 'nfpa', label: 'NFPA LiNK', description: 'National Fire Protection Association — NFPA 1, NFPA 101, and more. (Integration coming soon)' },
  { id: 'custom', label: 'Custom Vendor', description: 'Any other code set vendor or custom code.' },
];

const FIRE_CODE_OPTIONS = [
  { value: '', label: '— Select —' },
  { value: 'IFC', label: 'IFC — International Fire Code' },
  { value: 'NFPA', label: 'NFPA — National Fire Protection Association' },
  { value: 'state_specific', label: 'State-Specific Code' },
  { value: 'custom', label: 'Custom' },
];

/**
 * @component VendorForm
 *
 * Purpose:
 * - Per-vendor section in the regulatory wizard step. For ICC, lets the user
 *   test credentials, load the catalog, and check off the books they want
 *   downloaded after provisioning.
 *
 * Props:
 * - vendor:   Current draft vendor record (credentials + selectedBooks).
 * - onChange: Wizard-state mutator; persists into Dexie via the draft service.
 * - onRemove: Removes this vendor from the wizard's vendor list.
 *
 * Behavior:
 * - Test Connection calls system-api `/api/code-set/preflight/icc/validate`.
 *   On success, the catalog is loaded automatically. Books are checkbox-
 *   selectable; selections persist in the draft.
 *
 * Data Source:
 * - hybrid — UI state (validation, catalog) is in-memory only; selectedBooks
 *   ride the wizard draft into Dexie like every other wizard field.
 *
 * Constraints:
 * - The wizard has no tenant id yet; uses pre-tenant preflight endpoints.
 *   Credentials are NEVER persisted server-side by these calls.
 */
function VendorForm({
  vendor,
  onChange,
  onRemove,
}: {
  vendor: WizardRegulatoryVendor;
  onChange: (v: WizardRegulatoryVendor) => void;
  onRemove: () => void;
}) {
  const [showSecret, setShowSecret] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [ui, setUi] = useState<VendorUiState>(EMPTY_UI);

  const selectedBooks = vendor.selectedBooks ?? [];
  const hasIccCreds = !!(vendor.clientId?.trim() && vendor.clientSecret?.trim());

  async function handleTestIcc() {
    if (!vendor.clientId?.trim() || !vendor.clientSecret?.trim()) {
      setUi({ ...EMPTY_UI, validation: 'invalid', validationMessage: 'Enter Client ID and Client Secret first.' });
      return;
    }
    setUi({ validation: 'testing', catalogLoading: false });
    try {
      const result = await api.codeSetVendors.preflightValidateIcc({
        clientId: vendor.clientId,
        clientSecret: vendor.clientSecret,
      });
      if (!result.valid) {
        setUi({ validation: 'invalid', validationMessage: result.message ?? 'ICC rejected the credentials.', catalogLoading: false });
        return;
      }
      // Auto-load catalog on success.
      setUi({ validation: 'valid', catalogLoading: true });
      try {
        const cat = await api.codeSetVendors.preflightCatalogIcc({
          clientId: vendor.clientId,
          clientSecret: vendor.clientSecret,
        });
        setUi({ validation: 'valid', catalogLoading: false, catalog: cat.books });
      } catch (catErr: any) {
        setUi({
          validation: 'valid',
          catalogLoading: false,
          catalogError: catErr?.message ?? 'Failed to load ICC catalog',
        });
      }
    } catch (err: any) {
      setUi({
        validation: 'invalid',
        catalogLoading: false,
        validationMessage: err?.message ?? 'Network error during ICC validation',
      });
    }
  }

  function toggleBook(book: WizardSelectedBook) {
    const exists = selectedBooks.some((b) => b.bookId === book.bookId);
    const next = exists
      ? selectedBooks.filter((b) => b.bookId !== book.bookId)
      : [...selectedBooks, book];
    onChange({ ...vendor, selectedBooks: next });
  }

  return (
    <div className="border border-gray-200 rounded-xl p-4 space-y-3 bg-gray-50">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-800">
            {vendor.vendorId === 'icc' ? 'ICC Digital Codes'
              : vendor.vendorId === 'nfpa' ? 'NFPA LiNK'
              : 'Custom Vendor'}
          </span>
          {vendor.vendorId === 'nfpa' && (
            <span className="px-1.5 py-0.5 text-[10px] font-medium bg-amber-100 text-amber-700 rounded">Coming Soon</span>
          )}
          {vendor.vendorId === 'icc' && ui.validation === 'valid' && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium bg-emerald-100 text-emerald-700 rounded">
              <CheckCircle2 className="h-3 w-3" /> Connected
            </span>
          )}
          {vendor.vendorId === 'icc' && ui.validation === 'invalid' && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium bg-red-100 text-red-700 rounded">
              <XCircle className="h-3 w-3" /> Invalid
            </span>
          )}
        </div>
        <button type="button" onClick={onRemove}
          className="text-gray-400 hover:text-red-500 transition-colors p-0.5">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>

      {vendor.vendorId === 'icc' && (
        <div className="space-y-2.5">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Client ID</label>
            <input type="text" value={vendor.clientId ?? ''}
              onChange={e => { onChange({ ...vendor, clientId: e.target.value }); setUi(EMPTY_UI); }}
              placeholder="e.g. icc-client-abc123"
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Client Secret</label>
            <div className="relative">
              <input type={showSecret ? 'text' : 'password'} value={vendor.clientSecret ?? ''}
                onChange={e => { onChange({ ...vendor, clientSecret: e.target.value }); setUi(EMPTY_UI); }}
                placeholder="Enter client secret"
                className="w-full px-3 py-1.5 pr-8 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500" />
              <button type="button" onClick={() => setShowSecret(s => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-1">Credentials are encrypted server-side. Never stored in the browser.</p>
          </div>

          {/* Test connection button */}
          <div className="flex items-center gap-2">
            <button type="button"
              onClick={handleTestIcc}
              disabled={!hasIccCreds || ui.validation === 'testing' || ui.catalogLoading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-orange-300 bg-white text-orange-700 hover:bg-orange-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              {ui.validation === 'testing'
                ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Testing…</>
                : <>Test connection &amp; load catalog</>}
            </button>
            {ui.validation === 'invalid' && ui.validationMessage && (
              <span className="text-xs text-red-600 truncate" title={ui.validationMessage}>{ui.validationMessage}</span>
            )}
          </div>

          {/* Catalog list */}
          {ui.validation === 'valid' && (
            <div className="mt-3 border border-gray-200 rounded-lg bg-white">
              <div className="flex items-center gap-1.5 px-3 py-2 border-b border-gray-100">
                <BookOpen className="h-3.5 w-3.5 text-gray-500" />
                <span className="text-xs font-semibold text-gray-700">Books to download</span>
                {ui.catalogLoading && <Loader2 className="h-3 w-3 ml-auto animate-spin text-gray-400" />}
                {!ui.catalogLoading && ui.catalog && (
                  <span className="ml-auto text-[10px] text-gray-400">
                    {selectedBooks.length} of {ui.catalog.length} selected
                  </span>
                )}
              </div>
              {ui.catalogError && (
                <div className="px-3 py-2 text-xs text-red-600 flex items-start gap-1.5">
                  <AlertCircle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                  <span>{ui.catalogError}</span>
                </div>
              )}
              {!ui.catalogLoading && ui.catalog && ui.catalog.length === 0 && (
                <p className="px-3 py-3 text-xs text-gray-500 italic">
                  No books are assigned to this client subscription. Contact ICC to grant access.
                </p>
              )}
              {!ui.catalogLoading && ui.catalog && ui.catalog.length > 0 && (
                <ul className="max-h-60 overflow-y-auto divide-y divide-gray-100">
                  {ui.catalog.map((book) => {
                    const checked = selectedBooks.some((b) => b.bookId === book.bookId);
                    return (
                      <li key={book.bookId}>
                        <label className="flex items-start gap-2 px-3 py-2 hover:bg-orange-50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleBook(book)}
                            className="mt-0.5 h-3.5 w-3.5 rounded border-gray-300 text-orange-500 focus:ring-orange-400"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-800 truncate">{book.title}</p>
                            <p className="text-[11px] text-gray-500">
                              <span className="font-medium">{book.bookId}</span>
                              {book.edition && <> · {book.edition}</>}
                              {typeof book.chapterCount === 'number' && <> · {book.chapterCount} chapters</>}
                            </p>
                          </div>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              )}
              {selectedBooks.length > 0 && (
                <div className="px-3 py-2 text-[11px] text-gray-500 border-t border-gray-100 bg-gray-50 rounded-b-lg">
                  Downloads start automatically after the department is provisioned. You can monitor progress from the
                  background-jobs indicator in the sidebar.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {vendor.vendorId === 'nfpa' && (
        <div className="space-y-2.5">
          <div className="flex items-start gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-700">NFPA LiNK integration is not yet available. You can save credentials now for future use.</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">API Key</label>
            <div className="relative">
              <input type={showApiKey ? 'text' : 'password'} value={vendor.apiKey ?? ''} onChange={e => onChange({ ...vendor, apiKey: e.target.value })}
                placeholder="Enter NFPA API key"
                className="w-full px-3 py-1.5 pr-8 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500" />
              <button type="button" onClick={() => setShowApiKey(s => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Subscription ID <span className="font-normal text-gray-400">(optional)</span></label>
            <input type="text" value={vendor.subscriptionId ?? ''} onChange={e => onChange({ ...vendor, subscriptionId: e.target.value })}
              placeholder="e.g. NFPA-2024-PRO"
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500" />
          </div>
        </div>
      )}

      {vendor.vendorId === 'custom' && (
        <p className="text-xs text-gray-500">Custom vendor credentials can be configured after department creation in the Code Sets tab.</p>
      )}
    </div>
  );
}

export default function RegulatoryStep({ state, onChange }: Props) {
  const reg = state.regulatory ?? { vendors: [] };
  const vendors = reg.vendors ?? [];

  // Local-only file state for pending uploads.
  // File objects are not serializable, so they are never stored in wizard state.
  // Only the file names (metadata) are written to state for display purposes.
  const [feeScheduleFiles, setFeeScheduleFiles] = React.useState<File[]>([]);
  const [ordinanceFiles, setOrdinanceFiles] = React.useState<File[]>([]);

  function handleFeeScheduleFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []).slice(0, 3);
    setFeeScheduleFiles(files);
    onChange({ pendingFeeScheduleFileNames: files.map(f => f.name) });
  }

  function handleOrdinanceFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []).slice(0, 2);
    setOrdinanceFiles(files);
    onChange({ pendingOrdinanceFileNames: files.map(f => f.name) });
  }

  function updateReg(patch: Partial<typeof reg>) {
    onChange({ regulatory: { ...reg, ...patch } });
  }

  function addVendor(vendorId: WizardCodeSetVendorId) {
    if (vendors.some(v => v.vendorId === vendorId)) return;
    updateReg({ vendors: [...vendors, { vendorId }] });
  }

  function updateVendor(idx: number, v: WizardRegulatoryVendor) {
    const next = [...vendors];
    next[idx] = v;
    updateReg({ vendors: next });
  }

  function removeVendor(idx: number) {
    updateReg({ vendors: vendors.filter((_, i) => i !== idx) });
  }

  const availableToAdd = VENDOR_OPTIONS.filter(o => !vendors.some(v => v.vendorId === o.id));

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Regulatory Framework</h2>
        <p className="text-sm text-gray-500 mt-1">
          Configure the primary fire code and optional vendor credentials for digital code sets.
          Code set downloads can be triggered after department creation from the Code Sets tab.
        </p>
      </div>

      {/* Primary fire code */}
      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Adopted Code</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Primary Fire Code</label>
            <select value={reg.primaryFireCode ?? ''} onChange={e => updateReg({ primaryFireCode: (e.target.value as any) || undefined })}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500">
              {FIRE_CODE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Code Edition <span className="font-normal text-gray-400">(optional)</span></label>
            <input type="text" value={reg.codeEdition ?? ''} onChange={e => updateReg({ codeEdition: e.target.value || undefined })}
              placeholder="e.g. 2021"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500" />
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Jurisdiction Variant <span className="font-normal text-gray-400">(optional)</span></label>
            <input type="text" value={reg.jurisdictionVariant ?? ''} onChange={e => updateReg({ jurisdictionVariant: e.target.value || undefined })}
              placeholder="e.g. California amendments, NYC Local Law"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500" />
          </div>
        </div>
      </div>

      {/* Vendor credentials */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Code Set Vendors</h3>
          <span className="text-xs text-gray-400">Optional — can be added later</span>
        </div>

        {vendors.length > 0 && (
          <div className="space-y-3">
            {vendors.map((v, idx) => (
              <VendorForm key={v.vendorId} vendor={v} onChange={nv => updateVendor(idx, nv)} onRemove={() => removeVendor(idx)} />
            ))}
          </div>
        )}

        {availableToAdd.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs text-gray-500">Add a vendor to configure credentials for digital code access:</p>
            <div className="grid grid-cols-1 gap-2">
              {availableToAdd.map(opt => (
                <button key={opt.id} type="button" onClick={() => addVendor(opt.id)}
                  className="flex items-start gap-3 p-3 border border-dashed border-gray-300 rounded-xl text-left hover:border-orange-400 hover:bg-orange-50/50 transition-colors">
                  <Plus className="h-4 w-4 text-orange-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-gray-800">{opt.label}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{opt.description}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {vendors.length === 0 && availableToAdd.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-4">All available vendors have been added.</p>
        )}
      </div>

      {/* Fee Schedules & Ordinances */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
            Fee Schedules &amp; Ordinances
          </h3>
          <span className="text-xs text-gray-400">Optional — can be uploaded later</span>
        </div>

        {/* Info banner */}
        <div className="flex items-start gap-2 p-3 bg-orange-50 border border-orange-200 rounded-lg">
          <Info className="h-4 w-4 text-orange-500 mt-0.5 flex-shrink-0" />
          <div className="space-y-1">
            <p className="text-xs text-orange-700 font-medium">
              Fee schedule documents will be uploaded after the department is provisioned.
            </p>
            <p className="text-xs text-orange-600">
              You can also upload these documents later from the department’s{' '}
              <strong>Documents</strong> tab.
            </p>
          </div>
        </div>

        {/* Fee Schedules upload area */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Fee Schedules{' '}
            <span className="font-normal text-gray-400">(up to 3 files — PDF, DOCX, TXT)</span>
          </label>
          <div className="border border-gray-200 rounded-xl p-3 bg-gray-50 space-y-2">
            <input
              type="file"
              multiple
              accept=".pdf,.docx,.txt"
              onChange={handleFeeScheduleFiles}
              className="block w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-medium file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100 cursor-pointer"
            />
            {feeScheduleFiles.length > 0 && (
              <ul className="space-y-1">
                {feeScheduleFiles.map((file, i) => (
                  <li key={i} className="flex items-center gap-2 text-xs text-gray-600">
                    <FileText className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                    <span className="truncate">{file.name}</span>
                    <span className="text-gray-400 shrink-0">
                      ({(file.size / 1024 / 1024).toFixed(2)} MB)
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Ordinances upload area */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Ordinances{' '}
            <span className="font-normal text-gray-400">(up to 2 files — PDF, DOCX, TXT)</span>
          </label>
          <div className="border border-gray-200 rounded-xl p-3 bg-gray-50 space-y-2">
            <input
              type="file"
              multiple
              accept=".pdf,.docx,.txt"
              onChange={handleOrdinanceFiles}
              className="block w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-medium file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100 cursor-pointer"
            />
            {ordinanceFiles.length > 0 && (
              <ul className="space-y-1">
                {ordinanceFiles.map((file, i) => (
                  <li key={i} className="flex items-center gap-2 text-xs text-gray-600">
                    <FileText className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                    <span className="truncate">{file.name}</span>
                    <span className="text-gray-400 shrink-0">
                      ({(file.size / 1024 / 1024).toFixed(2)} MB)
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-xs text-blue-700">
          <strong>Note:</strong> Use <em>Test connection &amp; load catalog</em> to verify ICC credentials and check off the books you
          want pre-loaded for this department. Selected books are queued for download immediately after the department
          is provisioned and progress is visible from the background-jobs indicator in the sidebar.
        </p>
      </div>
    </div>
  );
}
